import ko from "knockout";
import _ from "lodash";
import audioUtils from "util/ininAudioUtils";
import VolumeControlView from "viewModels/widgets/audio/VolumeControlView";
import loggingService from "services/loggingService";
import toastService from "services/toastService";
import qualityAuthorization from "services/qualityAuth";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import speechAndTextAnalyticsService from "services/speechAndTextAnalyticsService";
import AnnotationView from "viewModels/AnnotationView";
import ViewModel from "viewModels/ViewModel";
import PlaybackHeaderView from "viewModels/PlaybackHeaderView";
import qualityBroadcast from "services/qualityBroadcast";
import { maxDescriptionChar } from "models/Annotation";
import TranscriptUIAnnotationTypeResources from "enums/TranscriptUIAnnotationTypeResources";
import AudioPlayerCoolDownHandler from "viewModels/widgets/audio/AudioPlayerCoolDownHandler";

/**
 * Per-recording reload state storage.
 * Each recording ID maps to its own maxReloads observable, allowing independent
 * retry tracking across multiple recordings. This prevents one recording's errors
 * from exhausting the reload budget of other recordings.
 */
const globalMaxReloads = {};
const DEFAULT_MAX_RELOADS = 3;

const AudioPlayerView = ViewModel.extend(function (
    audioUri,
    audioId,
    options,
    activeRecordingId
) {
    const self = this;
    const audioPlayers = ko.observableArray();
    const annotationIconWidth = 20;
    const resumePlayingDebounceTimer = 10;
    const isSeekingDebounceTimer = 400;
    const playerSyncDebounceTimer = 200;

    self.audioId = ko.observable(activeRecordingId);
    self.annotationsList = ko.observableArray();
    self.sentimentsList = ko.observableArray();
    self.isAudioLoaded = ko.observable(false);
    self.isError = ko.observable(false);
    self.isPlaying = ko.observable(false);
    self.isSeeking = ko.observable(false);
    self.isScrubbing = ko.observable(false);
    self.annotationViews = ko.observableArray([]);
    self.percentPosition = ko.observable(0);
    self.progressDisplay = ko.observable(0);
    self.durationDisplay = ko.observable(0);
    self.maxDescriptionChar = maxDescriptionChar;
    self.annotationTextInput = ko
        .observable("")
        .extend({ trimmed: null, maxLength: maxDescriptionChar });
    self.annotationClickLocation = ko
        .observable(0)
        .extend({ notify: "always" });
    self.playbackScrubberAriaValueText = ko.observable("");
    self.shouldSuggestWebm = audioUtils.shouldSuggestWebm();
    self.reloadCallback = null;
    self.currentPlaySpeed = ko.observable(1);
    self.lastPositionWhenStalled = 0;
    self.wasPlayingWhenStalled = false;

    self.autoPlay = false;
    self.darkMode = false;
    self.showAnnotations = true;
    self.isVoicemail = false;

    //Possible options:
    //{
    //  autoPlay            // Disables automatic play on load
    //  reloadCallback:     // Function to call if child view wants to reload the view stack
    //  darkMode:           // Display audioplayer with dark color scheme
    //  showAnnotations:    // Show (or not) the controls for adding annotations
    //  isVoicemail:        // Small CSS modifications for voicemails
    //  stalledData:        // last values for audio position, audio speed and playing state when stalled event occurs
    //}

    _.assign(self, options);

    if (activeRecordingId) {
        if (!globalMaxReloads[activeRecordingId]) {
            globalMaxReloads[activeRecordingId] =
                ko.observable(DEFAULT_MAX_RELOADS);
        }
        self.maxReloads = globalMaxReloads[activeRecordingId];
    } else {
        self.maxReloads = ko.observable(0); // No recording ID means we're not tracking maxReloads
    }

    self.header = self.subView(
        PlaybackHeaderView,
        ko.i18n.renderString("playback.recording.progress"),
        self.isVoicemail ? "voicemail" : "call"
    );

    const canAddAnnotation =
        qualityAuthorization.canAddAnnotation() && self.showAnnotations;
    self.canAddAnnotation = ko.observable(canAddAnnotation);
    self.waveformOuterColor = ko.observable("#ffffff");

    if (self.darkMode) {
        self.waveformOuterColor("#252525");
    }

    self.audioChannels = ko.observableArray();
    self.hasBeenInitialized = false;

    self.setChannels = function (channels) {
        // Updated player requires internal channel to be first in the channels array for dual channel recordings
        const mapChannels =
            channels.length === 2 ? _.reverse(channels) : channels;

        self.audioChannels(
            _.map(mapChannels, function (channel, index) {
                if (!channel.audioUri) {
                    loggingService.error(
                        "AudioPlayerView set a channel that didn't have an audioUri.",
                        {
                            channel,
                        }
                    );
                }
                channel.volumeControl = self.subView(VolumeControlView, {
                    syncVolumeToUserSettings: index === 0,
                });
                channel.index = index;
                return channel;
            })
        );
    };

    //
    // Public Functions
    //

    self.playPauseClick = function (data, event) {
        if (event) {
            event.preventDefault();
        }

        if (self.isPlaying()) {
            self.pause();
            self.playerSync(self.progressDisplay(), false);
        } else {
            self.play();
        }
    };

    self.play = function () {
        _.each(audioPlayers(), function (audioPlayer, index) {
            audioPlayer.play();
            loggingService.debug("Playing audio channel " + index);
        });
        self.updateIsPlaying();
    };

    self.setSpeed = function (speed) {
        _.each(audioPlayers(), function (audioPlayer) {
            audioPlayer.setSpeed(speed);
        });
        self.updateIsPlaying();
    };

    self.pause = function () {
        _.each(audioPlayers(), function (audioPlayer) {
            audioPlayer.pause();
        });
        self.updateIsPlaying();
    };

    self.stop = function () {
        _.each(audioPlayers(), function (audioPlayer) {
            audioPlayer.stop();
        });
        self.updateIsPlaying();
    };

    self.shouldDrawFromData = self.computed(function () {
        return _.every(self.audioChannels(), function (channel) {
            return (
                channel.waveformData &&
                _.without(channel.waveformData, undefined, null, "").length > 0
            );
        });
    });

    self.waveformClick = function (percentPosition, event) {
        if (event) {
            event.preventDefault();
        }
        if (audioPlayers().length > 0) {
            // All audio players should have matching duration and position.
            const newPosition =
                audioPlayers()[0].getDuration() * percentPosition;
            self.setPositionMs(Math.max(0, newPosition));
            self.updatePosition(audioPlayers()[0].getPosition(), "fastforward");
            self.debouncedPlayerSync(newPosition, true);
        }
    };

    self.matchSentiment = function (waveformLocation) {
        // Jump to the first occurrence found
        _.find(self.sentimentsList(), function (sentiment) {
            if (sentiment.recordingLocation === waveformLocation) {
                const annotationClickEvent = {
                    phraseId: sentiment.phraseId,
                    topicNumber: sentiment.topicNumber
                        ? sentiment.topicNumber
                        : null,
                };
                qualityBroadcast.publish(
                    activeRecordingId,
                    "transcriptUIAnnotationClick",
                    annotationClickEvent
                );
                self.setPositionMs(Math.max(0, sentiment.recordingLocation));
                self.updatePosition(sentiment.recordingLocation, "fastforward");
                self.updateWaveformScrollPosition();
                self.playerSync(sentiment.recordingLocation, true);
                return true;
            }
            return false;
        });
    };

    self.playerSync = function (position, isManualSync) {
        qualityBroadcast.publish(
            activeRecordingId,
            "transcriptUIPositionUpdate",
            {
                currentPositionMs: position,
                isPlaying: self.isPlaying(),
                hasPriority: isManualSync,
            }
        );
    };

    self.debouncedPlayerSync = _.debounce(
        (position, isManualSync) => {
            self.playerSync(position, isManualSync);
        },
        playerSyncDebounceTimer,
        { leading: true }
    );

    self.setPositionMs = function (positionInMs) {
        if (audioPlayers().length > 0) {
            _.each(audioPlayers(), function (audioPlayer) {
                // Pause before seeking, but do not "updateIsPlaying"
                // Then, as soon as all channels can play again, it will resume.
                audioPlayer.pause();
                audioPlayer.setPosition(positionInMs);
            });
        }
    };

    self.getDuration = function () {
        if (audioPlayers().length > 0) {
            const audioPlayer = audioPlayers()[0];
            return audioPlayer.getDuration();
        }
        return 0;
    };

    self.getPosition = function () {
        if (audioPlayers().length > 0) {
            const audioPlayer = audioPlayers()[0];
            return audioPlayer.getPosition();
        }
        return 0;
    };

    /**
     * Converts a duration in milliseconds to a human-readable time string
     * using the Intl.DurationFormat API for proper localization
     *
     * @param {number} duration - The duration in milliseconds (must be >= 1000ms)
     * @returns {string} Localized duration string (e.g., "2 hours 30 minutes 15 seconds")
     *
     * @example
     * humanizeDuration(9000000) // Returns "2 hours 30 minutes" (in English)
     * humanizeDuration(0) // Returns "0 seconds" (in English)
     */
    const humanizeDuration = function (duration) {
        // Intl.DurationFormat is widely supported in all browsersfrom March 2025 only, and is not available in older versions
        if (typeof Intl.DurationFormat !== "function") {
            loggingService.warn("Intl.DurationFormat is not supported");
            return "";
        }

        // Validate duration
        if (
            !duration ||
            typeof duration !== "number" ||
            isNaN(duration) ||
            duration === Infinity
        ) {
            return "";
        }

        // Normalize duration
        if (duration < 1000) {
            duration = 0;
        }

        const locale = ko.i18n.getLocale();
        const options = {
            style: "long",
            secondsDisplay: duration === 0 ? "always" : "auto",
        };

        const durationFormatterIntl = new Intl.DurationFormat(locale, options);

        // Get duration values and convert to integers, handling falsy values
        const durationValues = ko.i18n.formatDurationWithUnit(duration);
        const [years, days, hours, minutes, seconds] = durationValues.map(
            (val) => (val ? parseInt(val, 10) : 0)
        );

        return durationFormatterIntl.format({
            years,
            days,
            hours,
            minutes,
            seconds,
        });
    };

    const cachedHumanizeDuration = _.memoize((duration) =>
        humanizeDuration(duration)
    );

    const updateScrubberAriaValueText = _.debounce(function () {
        try {
            // Don't memoize position since it updates frequently
            const positionString = humanizeDuration(self.getPosition());

            // Cache duration since it only changes on audio load
            const durationString = cachedHumanizeDuration(self.getDuration());

            if (positionString === "" || durationString === "") {
                self.playbackScrubberAriaValueText("");
                return;
            }

            const translatedString = ko.i18n.renderString(
                "playback.controls.scrubber.valueText",
                {
                    position: positionString,
                    duration: durationString,
                }
            );

            self.playbackScrubberAriaValueText(translatedString);
        } catch (e) {
            loggingService.error("Error updating scrubber aria value text", e);
            self.playbackScrubberAriaValueText("");
        }
    }, 100);

    const getExpiresTimestamp = (audioUri) => {
        if (!audioUri) {
            return null;
        }

        try {
            const url = new URL(audioUri);

            // Check regular search params
            const expiresParam = url.searchParams.get("Expires");

            if (!expiresParam) {
                return null;
            }

            const parsed = parseInt(expiresParam);
            return !isNaN(parsed) && parsed > 0 ? parsed : null;
        } catch (error) {
            loggingService.warn(
                "Error parsing URL or expires parameter:",
                error
            );
            return null;
        }
    };

    /**
     * Checks if the audio URI has expired based on the `Expires` query parameter.
     * Used to differentiate between actual playback errors and TTL expiry issues.
     * When TTL expires, we allow unlimited reloads since it's not a true error.
     */
    const isAudioUriExpired = (audioUri) => {
        if (!audioUri) {
            return false;
        }
        // Extract expires timestamp from URL.
        const expiresTimestamp = getExpiresTimestamp(audioUri);
        const currentTimestamp = Math.floor(Date.now() / 1000); // Convert to seconds
        return expiresTimestamp && currentTimestamp >= expiresTimestamp;
    };

    const annotations = function () {
        let lastPercentPosition = 0;
        let lastAnnotationPosition = 0;
        let newAnnotationPosition = 0;

        function showAnnotation(annotation) {
            const tooltipDuration = 2000;
            annotation.showTooltip();

            setTimeout(function () {
                annotation.hideTooltip();
            }, tooltipDuration);
        }

        return {
            doUpdate: function (percentPosition) {
                _.forEach(self.annotationViews(), function (annotation) {
                    if (
                        percentPosition - lastPercentPosition < 100 &&
                        lastPercentPosition < annotation.percentPosition() &&
                        annotation.percentPosition() < percentPosition &&
                        !self.isSeeking()
                    ) {
                        showAnnotation(annotation);
                        lastAnnotationPosition = newAnnotationPosition;
                        newAnnotationPosition = annotation.percentPosition();
                    } else if (
                        lastAnnotationPosition ==
                            annotation.percentPosition() &&
                        newAnnotationPosition != annotation.percentPosition()
                    ) {
                        annotation.hideTooltip();
                    }
                });

                lastPercentPosition = percentPosition;
            },
            updatePosition: (percentPosition) =>
                (lastPercentPosition = percentPosition),
        };
    };

    const annotation = annotations();

    self.updatePosition = function (position, source) {
        updateScrubberAriaValueText();
        if (self.isPlaying()) {
            self.playerSync(position, false);
        }
        if (audioPlayers().length > 0) {
            const audioPlayer = audioPlayers()[0];

            //If trying to seek past the end of the call, prevent resetting position back to 0
            if (
                self.isSeeking() &&
                self.percentPosition() === 100 &&
                position === 0
            ) {
                return;
            }
            self.percentPosition((position / audioPlayer.getDuration()) * 100);

            if (isNaN(self.percentPosition())) {
                self.percentPosition(0);
            }

            self.progressDisplay(position);
            self.header.setProgress(position);
            if (source === "fastforward") {
                annotation.updatePosition(self.percentPosition());
            } else {
                annotation.doUpdate(self.percentPosition());
            }

            if (self.isPlaying()) {
                self.updateWaveformScrollPosition();
            }
        }
    };

    self.updateWaveformScrollPosition = function () {
        const waveformOverlayEle = document.getElementsByClassName(
            "playback-waveform-overlay"
        )[0];
        const waveformEle = document.getElementsByClassName("waveContainer")[0];
        if (waveformOverlayEle && waveformEle) {
            const scrollPositionPx =
                waveformOverlayEle.offsetWidth *
                    (self.indicatorPosition() / 100) -
                waveformEle.offsetWidth / 2;
            document.getElementsByClassName("playback-waveform")[0].scrollLeft =
                scrollPositionPx;
        }
    };

    self.indicatorPosition = self.computed(function () {
        const indicatorWidth = 9;
        const fullWaveformWidth = _.max(
            _.map(self.audioChannels(), (channel) => {
                if (!channel || !channel.waveformData) {
                    return 0;
                }
                return channel.waveformData.length;
            })
        );
        if (!fullWaveformWidth) {
            return self.percentPosition();
        }
        if (
            (self.percentPosition() / 100) * fullWaveformWidth <
            indicatorWidth / 2
        ) {
            return (indicatorWidth / 2 / fullWaveformWidth) * 100;
        } else if (
            (self.percentPosition() / 100) * fullWaveformWidth >
            fullWaveformWidth - indicatorWidth / 2
        ) {
            return (
                ((fullWaveformWidth - indicatorWidth / 2) / fullWaveformWidth) *
                100
            );
        } else {
            return self.percentPosition();
        }
    });

    self.updateIsPlaying = function () {
        if (audioPlayers().length > 0) {
            self.isPlaying(audioPlayers()[0].getIsPlaying());
        }
    };

    let savedPlayingState = null;
    // non-debounce function
    // call using self.handleIsScrubbing()
    self.isScrubbingHandler = (isScrubbing) => {
        self.isScrubbing(isScrubbing);
        if (isScrubbing) {
            //currently scrubbing, save playing state
            savedPlayingState = self.isPlaying();
            self.pause();
        } else {
            //releasing scrubber, resume playing
            savedPlayingState ? self.play() : self.pause();
            savedPlayingState = null;
        }
    };

    self.handleIsScrubbing = _.debounce(
        self.isScrubbingHandler,
        resumePlayingDebounceTimer
    );

    self.jumpToLocation = (payload) => {
        let location = 0;
        let activeTranscriptRecordingId = "";
        if (
            payload.transfer &&
            payload.transfer.location &&
            payload.transfer.activeRecordingId
        ) {
            location = payload.transfer.location;
            activeTranscriptRecordingId = payload.transfer.activeRecordingId;
        } else {
            location = payload.transfer;
        }
        if (
            !activeTranscriptRecordingId ||
            activeTranscriptRecordingId === activeRecordingId
        ) {
            self.setPositionMs(Math.max(0, location));
            self.updatePosition(location, "fastforward");
            self.updateWaveformScrollPosition();
        }
    };

    self.handlePlayControlRequest = (playControlRequest) => {
        playControlRequest ? self.play() : self.pause();
    };

    let reloadStarted = false;

    self.handleStalledEvent = function () {
        // Check global cooldown state
        if (!AudioPlayerCoolDownHandler.canExecute()) {
            return;
        }

        loggingService.info("Handling stalled event - URL expired");
        if (!reloadStarted) {
            loggingService.info("Reload started due to stalled event");
            reloadStarted = true;
            AudioPlayerCoolDownHandler.updateExecutionTime();
            toastService.notifyMessage(
                "playback.recordingPlayback.error.title",
                {
                    bodyMessageKey: "playback.recordingPlayback.error.msg",
                    messageData: {},
                    icon: "pc-icon-messages",
                }
            );

            if (self.reloadCallback && self.maxReloads() > 0) {
                const reloadData = {
                    lastPosition: self.lastPositionWhenStalled,
                    wasPlaying: self.wasPlayingWhenStalled,
                    lastPlayerSpeed: self.currentPlaySpeed(),
                    isFromStalledEvent: true,
                    maxReloads: self.maxReloads,
                };
                self.reloadCallback(reloadData);
            } else if (self.maxReloads() <= 0) {
                self.isError(true);
            }
        }
    };

    self.handleError = function (e, data) {
        let reload = false,
            isDueToTTLExpiry = false;
        self.isError(false);

        switch (e.target.error.code) {
            case e.target.error.MEDIA_ERR_ABORTED:
                loggingService.debug("Playback aborted.");
                reload = false;
                //Setting isError to true, the reloadCallback function is not called because reload is false.
                self.isError(true);
                break;
            case e.target.error.MEDIA_ERR_NETWORK:
                //This is what happens if the item cache goes missing during playback (after a long pause for example)
                loggingService.debug(
                    "A network error caused the audio download to fail."
                );
                reload = true;
                break;
            case e.target.error.MEDIA_ERR_DECODE:
                loggingService.debug(
                    "The audio playback was aborted due to a corruption problem or because the audio used features your browser did not support."
                );
                reload = true;
                break;
            case e.target.error.MEDIA_ERR_SRC_NOT_SUPPORTED:
                loggingService.debug(
                    "The video audio not be loaded, either because the server or network failed or because the format is not supported."
                );
                reload = true;
                // If the audio URI is expired, set isDueToTTLExpiry to true - can reload even if maxReloads is 0
                isDueToTTLExpiry = isAudioUriExpired(data?.audioUri);
                break;
            default:
                loggingService.debug("An unknown error occurred.");
                reload = true;
                break;
        }

        // Only show error if reloads are exhausted AND it's not a TTL expiry.
        // TTL expiry is expected behavior and should always trigger a reload.
        if (self.maxReloads() <= 0 && !isDueToTTLExpiry) {
            self.isError(true);
            reload = false;
        }

        if (reload && !reloadStarted) {
            reloadStarted = true;
            toastService.notifyMessage(
                "playback.recordingPlayback.error.title",
                {
                    bodyMessageKey: "playback.recordingPlayback.error.msg",
                    messageData: {},
                    icon: "pc-icon-messages",
                }
            );

            if (self.reloadCallback) {
                const reloadData = {
                    lastPosition: 0,
                    wasPlaying: false,
                    lastPlayerSpeed: 1,
                    isFromStalledEvent: false,
                    maxReloads: self.maxReloads, // Pass observable reference for decrement by parent
                    keepCurrentRecording: true, // Maintain current recording selection after reload
                    isDueToTTLExpiry: !!isDueToTTLExpiry, // If true, don't count against maxReloads
                };
                self.reloadCallback(reloadData);
            }
        }
    };

    self.init = function (audioPlayer, audioChannel, html5Node) {
        if (audioChannel.audioUri) {
            const eventTypes = audioPlayer.eventTypes;

            // event logging
            _.each(
                [
                    eventTypes.CANPLAYTHROUGH,
                    eventTypes.STALLED,
                    eventTypes.SEEKING,
                    eventTypes.SEEKED,
                ],
                function (eventType) {
                    audioPlayer.addListener(eventType, function () {
                        const logLevel =
                            eventType === eventTypes.STALLED ? "warn" : "info";
                        loggingService[logLevel](
                            "handling audio " + eventType + " event",
                            {
                                readyState: audioPlayer
                                    .getReadyState()
                                    .toString(),
                            }
                        );
                    });
                }
            );

            audioPlayer.addListener(eventTypes.READY, function () {
                audioPlayer.load(audioChannel.audioUri);
            });

            qualityBroadcast.subscribe(
                activeRecordingId,
                "isScrubbing",
                (message) => {
                    self.handleIsScrubbing(message);
                }
            );

            qualityBroadcast.subscribe(
                activeRecordingId,
                "transcriptUIJumpToLocation",
                (message) => {
                    self.jumpToLocation(message);
                }
            );

            qualityBroadcast.subscribe(
                activeRecordingId,
                "transcriptUIPlayControlRequest",
                (message) => {
                    self.handlePlayControlRequest(message);
                }
            );

            // Safari doesn't send CANPLAYTHROUGH when seeking
            // ends. So we should listen for SEEKED also.
            _.each(
                [eventTypes.CANPLAYTHROUGH, eventTypes.SEEKED],
                function (eventType) {
                    audioPlayer.addListener(eventType, function () {
                        const allCanPlay = _.every(
                            audioPlayers(),
                            function (audioPlayer) {
                                // All channels have loaded enough to likely play through
                                // to the end of the file
                                return audioPlayer.getReadyState() == 4;
                            }
                        );

                        if (allCanPlay) {
                            setIsSeeking(false);
                            self.isAudioLoaded(true);
                            // Reset maxReloads on successful playback - recording is now stable
                            self.maxReloads(DEFAULT_MAX_RELOADS);
                            if (self.autoplay || self.isPlaying()) {
                                self.play();

                                // only autoplay with the initial load
                                self.autoplay = false;
                            }
                            self.updateIsPlaying();
                        }
                    });
                }
            );

            audioPlayer.addListener(eventTypes.LOADEDDATA, function () {
                if (self.percentPosition() == 0) {
                    self.updatePosition(0);
                }
                const stalledData = self.stalledData;
                if (stalledData && stalledData.lastPositionWhenStalled != 0) {
                    const newPosition = stalledData.lastPositionWhenStalled;
                    self.setPositionMs(Math.max(0, newPosition));
                    self.updatePosition(newPosition);
                    self.debouncedPlayerSync(newPosition, true);
                    self.currentPlaySpeed(stalledData.playerSpeedWhenStalled);
                    if (stalledData.wasPlayingWhenStalled) {
                        self.play();
                    }
                    // reset stalled event data
                    self.lastPositionWhenStalled = 0;
                    self.playerSpeedWhenStalled = 1;
                    self.wasPlayingWhenStalled = false;
                }
            });

            // Only listen to time updates on one channel, to smooth out jitters in duration display
            if (audioChannel.index === 0) {
                // Handles time changes from the player (ex. updates the time every tick on the player)
                audioPlayer.addListener(
                    eventTypes.TIMEUPDATE,
                    function (position) {
                        /* Only time the html5AudioElement in ininAudioPlayer will set the TIMEUPDATE to 0 is when initialized in Chrome.
                       This is handled in LOADEDDATA however, so this can be blocked.
                       The only other time this can happen is when manually "scrubbing" on the player. This is handled in the SEEKED event
                       This conditional does not update the position when the audio player wants to update to 0,
                       because switching between recordings will reset the position to 0 */
                        if (position != 0) {
                            self.updatePosition(position);
                        }
                    }
                );
                // Handles time changes from manual intervention (i.e. clicking through the waveform)
                audioPlayer.addListener(eventTypes.SEEKED, function (position) {
                    self.updatePosition(position);
                });
            }

            audioPlayer.addListener(eventTypes.SEEKING, function () {
                setIsSeeking(true);
            });

            audioPlayer.addListener(eventTypes.STALLED, function () {
                self.isAudioLoaded(false);
                // Only handle stalled event if URL has expired or about to expire
                if (isAudioUriExpired(audioChannel?.audioUri)) {
                    self.lastPositionWhenStalled = audioPlayer.getPosition();
                    self.wasPlayingWhenStalled = audioPlayer.getIsPlaying();
                    self.handleStalledEvent();
                } else {
                    loggingService.info(
                        "Stalled event ignored - URL has not expired yet"
                    );
                }
            });

            audioPlayer.addListener(eventTypes.LOG, function (message) {
                loggingService.debug(message);
            });

            audioPlayer.addListener(eventTypes.ENDED, function () {
                self.updatePosition(0);
                self.updateIsPlaying();
                self.playerSync(0, false);
            });

            audioPlayer.init(html5Node, self.mimeType);

            if (audioPlayers().length < self.audioChannels().length) {
                audioPlayers.push(audioPlayer);
            }

            // Skips doing the sync if both the feature toggle is on and the first time recording loaded
            const shouldSkipPlayerSync =
                featureToggleService.isEnabled(
                    FeatureToggle.WEM_1249_LOAD_TRANSCRIPT_WITHOUT_RECORDING
                ) && !self.hasBeenInitialized;

            // Only first channel handles sync (redundant to do for each channel)
            if (
                audioChannel.index === 0 &&
                !shouldSkipPlayerSync &&
                !isNaN(self.getDuration()) &&
                !isNaN(self.percentPosition())
            ) {
                self.playerSync(
                    self.getDuration() * (self.percentPosition() / 100),
                    true
                );
            }

            // Mark as initialized after first channel
            if (audioChannel.index === 0) {
                self.hasBeenInitialized = true;
            }

            self.computed(function () {
                if (audioChannel.volumeControl) {
                    if (!isNaN(audioChannel.volumeControl.volumeLevel())) {
                        audioPlayer.setVolumeLevel(
                            audioChannel.volumeControl.volumeLevel()
                        );
                    }
                } else {
                    loggingService.warn(
                        "No volume control for audio channel with uri " +
                            audioChannel.audioUri
                    );
                }
            });
        }
    };

    //
    // Computed functions
    //
    self.computed(function () {
        if (
            (self.annotationsList() || self.sentimentsList()) &&
            self.isAudioLoaded()
        ) {
            if (audioPlayers().length > 0) {
                const audioPlayer = audioPlayers()[0];
                const duration = audioPlayer.getDuration();
                self.durationDisplay(duration);
                self.header.setDuration(duration);
                self.sentimentsList(
                    speechAndTextAnalyticsService.sentimentsList()
                );
                updateAnnotations(duration);
            }
        }
    });

    self.subscribe(self.currentPlaySpeed, function (newVal) {
        self.setSpeed(newVal);
    });

    //
    // Private functions
    //

    const setIsSeeking = _.debounce(
        (isSeeking) => {
            self.isSeeking(isSeeking);
        },
        isSeekingDebounceTimer,
        { leading: true }
    );

    const updateAnnotations = function (totalAudioDurationInMs) {
        self.annotationViews([]);
        const fullWaveformWidth = _.max(
            _.map(self.audioChannels(), (channel) => {
                if (!channel || !channel.waveformData) {
                    return 0;
                }
                return channel.waveformData.length;
            })
        );

        //Annotations overlapping more than half of the icon width should be grouped together
        const minMsOverlapAllowed =
            (totalAudioDurationInMs / fullWaveformWidth) *
            (annotationIconWidth / 2);

        _.each(self.sentimentsList(), function (playbackEvent) {
            // Don't set the default recordingLocation if it already exists on the event, or if the event is a non-object (We use strings in some tests)
            if (
                playbackEvent.recordingLocation === null ||
                (playbackEvent.recordingLocation === undefined &&
                    typeof playbackEvent === "object" &&
                    playbackEvent !== null)
            ) {
                playbackEvent.recordingLocation = playbackEvent.location;
            }
            if (playbackEvent.recordingLocation <= totalAudioDurationInMs) {
                const existingTranscriptUIAnnotationView = _.find(
                    self.annotationViews(),
                    function (annotationView) {
                        if (
                            TranscriptUIAnnotationTypeResources[
                                annotationView.getType()
                            ] &&
                            TranscriptUIAnnotationTypeResources[
                                playbackEvent.type
                            ]
                        ) {
                            return (
                                Math.abs(
                                    annotationView.getLocation() -
                                        playbackEvent.recordingLocation
                                ) <= minMsOverlapAllowed
                            );
                        }
                    }
                );

                if (existingTranscriptUIAnnotationView) {
                    existingTranscriptUIAnnotationView.mergeTranscriptUIAnnotation(
                        playbackEvent
                    );
                } else {
                    self.annotationViews.push(
                        self.subView(
                            AnnotationView,
                            playbackEvent,
                            totalAudioDurationInMs,
                            fullWaveformWidth,
                            self.annotationClickLocation,
                            self.isSeeking
                        )
                    );
                }
            }
        });

        _.each(self.annotationsList(), function (playbackEvent) {
            const location = ko.utils.unwrapObservable(
                playbackEvent.recordingLocation
            );

            if (location <= totalAudioDurationInMs) {
                //Group together any annotations that are overlapping each other
                const existingAnnotationView = _.find(
                    self.annotationViews(),
                    function (annotationView) {
                        return (
                            Math.abs(annotationView.getLocation() - location) <=
                            minMsOverlapAllowed
                        );
                    }
                );

                //If there was another annotation view at the same time add it to the existing one
                //otherwise, create a new one.
                if (existingAnnotationView) {
                    existingAnnotationView.addAnnotation(playbackEvent);
                } else {
                    self.annotationViews.push(
                        self.subView(
                            AnnotationView,
                            playbackEvent,
                            totalAudioDurationInMs,
                            fullWaveformWidth,
                            self.annotationClickLocation,
                            self.isSeeking
                        )
                    );
                }
            }
        });
    };

    self.subscribe(self.annotationClickLocation, function (location) {
        if (typeof location === "number") {
            if (audioPlayers().length > 0) {
                // Check if clicked position matches up with a nearby annotation
                self.matchSentiment(location);
            }
        }
    });

    /**
     * CLEAN UP
     */
    self.onDispose = function () {
        _.each(audioPlayers(), function (audioPlayer) {
            audioPlayer.reset();
        });
    };
});

export default AudioPlayerView;
